import React from "react";
import { Link } from "react-router-dom";
import { IPostRunSummary, runSportLabel, runSummaryText } from "@eagerdog/interfaces";

import Icon from "src/components/Icon/Icon";

import styles from "./RunSummary.module.scss";

interface IProps {
    run: IPostRunSummary;
    className?: string;
}

/** "AKC Scent Work" etc. — the same label the Ribbon Room sport cards use. */
export const sportLabel = runSportLabel;

/**
 * Feed card run block. All text comes from `runSummaryText` so the card, the
 * share description / OG tags and the result emails stay identical; this
 * component only adds the event link and layout.
 */
const RunSummary: React.FC<IProps> = ({ run, className }) => {
    const text = runSummaryText(run);

    // The event name links to the event page when the run came from one.
    const whereParts: React.ReactNode[] = text.whereParts.map((part) =>
        part === run.event_name && run.event_handle
            ? <Link to={`/e/${run.event_handle}`} className={styles.eventLink}>{part}</Link>
            : part
    );

    return (
        <div className={[styles.RunSummary, className || ""].filter(Boolean).join(" ")}>
            <div className={styles.top}>
                <div className={styles.text}>
                    <div className={styles.sport}>{text.sport}</div>
                    {text.classLine && <div className={styles.classLine}>{text.classLine}</div>}
                    {whereParts.length > 0 && (
                        <div className={styles.whereLine}>
                            {whereParts.map((part, i) => (
                                <React.Fragment key={i}>{i > 0 && " · "}{part}</React.Fragment>
                            ))}
                        </div>
                    )}
                </div>
                {text.result && (
                    <span className={[styles.result, text.qualified ? styles.qualified : styles.notQualified].join(" ")}>
                        {text.result}
                    </span>
                )}
            </div>

            {(text.stats.length > 0 || (run.awards && run.awards.length > 0)) && (
                <div className={styles.statsRow}>
                    {text.stats.map(([label, value]) => (
                        <div className={styles.stat} key={label}>
                            <div className={styles.statValue}>{value}</div>
                            <div className={styles.statLabel}>{label}</div>
                        </div>
                    ))}
                    {run.awards?.map((award) => (
                        <span className={styles.award} key={award}>{award}</span>
                    ))}
                </div>
            )}

            {(run.judge_name || run.event_handle) && (
                <div className={styles.footer}>
                    {run.judge_name && <span className={styles.judge}>Judge: {run.judge_name}</span>}
                    {run.event_handle && (
                        <Link to={`/e/${run.event_handle}`} className={styles.viewEvent}>
                            View event results
                            <Icon type="caretRight" className={styles.viewEventIcon} />
                        </Link>
                    )}
                </div>
            )}
        </div>
    );
};

export default RunSummary;
